import { useState } from 'react';
import { Alert, Badge, Button, Checkbox, Group, Modal, ScrollArea, Stack, Table, TagsInput, Text } from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { notifications } from '@mantine/notifications';
import { httpsCallable } from 'firebase/functions';
import dayjs from 'dayjs';
import { useFunctions } from '../../../helpers/firebaseContext';
import { LsegCorporateActionsConfig } from '../../../types';

// Mirrors CaItem in shared-helpers/src/lsegCorporateActions.ts (+ CaPreviewItem extras from functions-geneva).
type CaItem = {
  key: string;
  type: 'Dividend' | 'Split';
  investment: string;
  description: string;
  exchange: string;
  exDate: string;
  settleDate: string | null;
  perShareAmount?: number;
  newShares?: number;
  oldShares?: number;
  comments: string;
  missing: string[];
  possibleDuplicates?: string[];
  existingRequestId?: string; // set when LSEG changed something on a CA we already have
  existingStatus?: string;
  changes?: { field: string; from: string; to: string }[];
  holdingPortfolios?: number;
};
type Preview = {
  from: string;
  to: string;
  today: string;
  items: CaItem[];
  hidden: number;
  paid: number;
  missingRic: { id: string; CODE: string; exchange: string }[];
  notes: string[];
};

const fmt = (d?: string | null) => (d ? dayjs(d.slice(0, 10)).format('DD/MM/YYYY') : '—');
const selectable = (i: CaItem) => !i.missing.length;
// a possible duplicate usually means a wrong date somewhere, so a person ticks it on purpose
const preselected = (i: CaItem) => selectable(i) && !i.possibleDuplicates?.length;

export function LsegCorporateActionsModal({ opened, onClose, config }: {
  opened: boolean;
  onClose: () => void;
  config: LsegCorporateActionsConfig;
}) {
  const functions = useFunctions();
  const [from, setFrom] = useState<string | null>(config.exDateCutoff ?? dayjs().format('YYYY-MM-DD'));
  const [to, setTo] = useState<string | null>(dayjs().add(1, 'year').format('YYYY-MM-DD'));
  const [exchanges, setExchanges] = useState<string[]>(config.exchanges ?? []);
  const [includePaid, setIncludePaid] = useState(false);
  const [preview, setPreview] = useState<Preview | null>(null);
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [loading, setLoading] = useState<'fetch' | 'add' | null>(null);

  const retrieve = async () => {
    setLoading('fetch');
    setPreview(null);
    try {
      const { data } = await httpsCallable<{ from: string; to: string; exchanges: string[]; includePaid: boolean }, Preview>(
        functions, 'lsegCorporateActionsPreview', { timeout: 310_000 },
      )({ from: from!, to: to!, exchanges, includePaid });
      setPreview(data);
      setSelected(new Set(data.items.filter(preselected).map((i) => i.key)));
    } catch (e: any) {
      notifications.show({ title: 'LSEG fetch failed', message: e?.message || String(e), color: 'red' });
    } finally {
      setLoading(null);
    }
  };

  const add = async () => {
    // keep the server's order: a same-day dividend is created before its split
    const items = preview!.items.filter((i) => selected.has(i.key));
    setLoading('add');
    try {
      const { data } = await httpsCallable<{ items: CaItem[] }, { added: string[]; updated: string[]; skipped: string[] }>(
        functions, 'lsegCorporateActionsAdd', { timeout: 310_000 },
      )({ items });
      notifications.show({
        title: 'Corporate actions sent for approval',
        message: `${data.added.length} new request(s) created by CRM Automation` +
          (data.updated.length ? `, ${data.updated.length} existing request(s) updated with LSEG's changes` : '') +
          (data.skipped.length ? `, ${data.skipped.length} skipped (already there)` : '') + '.',
        color: 'green',
      });
      setPreview(null);
      onClose();
    } catch (e: any) {
      notifications.show({ title: 'Adding corporate actions failed', message: e?.message || String(e), color: 'red' });
    } finally {
      setLoading(null);
    }
  };

  const selectableKeys = preview?.items.filter(selectable).map((i) => i.key) ?? [];
  const allSelected = selectableKeys.length > 0 && selectableKeys.every((k) => selected.has(k));
  const toggle = (key: string) => setSelected((prev) => {
    const next = new Set(prev);
    if (next.has(key)) next.delete(key); else next.add(key);
    return next;
  });

  return (
    <Modal opened={opened} onClose={onClose} title="Get corporate actions from LSEG" size="90%">
      <Stack>
        <Group align="flex-end">
          <DatePickerInput
            label="Ex-date from"
            description={config.exDateCutoff ? `Cutoff in config: ${fmt(config.exDateCutoff)}` : undefined}
            value={from}
            onChange={(v) => setFrom(v as string | null)}
            valueFormat="DD/MM/YYYY"
            w={200}
          />
          <DatePickerInput
            label="Ex-date to"
            value={to}
            onChange={(v) => setTo(v as string | null)}
            minDate={from ?? undefined}
            valueFormat="DD/MM/YYYY"
            w={200}
          />
          <TagsInput
            label="Markets (EXCHANGE_CODE)"
            value={exchanges}
            onChange={setExchanges}
            style={{ flex: 1 }}
          />
          <Button onClick={retrieve} loading={loading === 'fetch'} disabled={!from || !to || to < from || !exchanges.length || loading === 'add'}>
            Retrieve
          </Button>
        </Group>
        <Group justify="space-between" align="flex-start">
          <Text size="xs" c="dimmed" style={{ flex: 1 }}>
            Cash dividends and splits with an ex-date in this range that are not paid yet (payment date today or later).
            Ones already in Geneva requests are hidden unless LSEG changed them.
          </Text>
          <Checkbox
            size="xs"
            label="Include already paid"
            checked={includePaid}
            onChange={(e) => setIncludePaid(e.currentTarget.checked)}
          />
        </Group>

        {preview && preview.missingRic.length > 0 && (
          <Alert color="yellow" title={`${preview.missingRic.length} securities have no RIC, so their corporate actions can't be fetched`}>
            <ScrollArea.Autosize mah={80}>
              <Text size="xs">{preview.missingRic.map((s) => s.id).join(', ')}</Text>
            </ScrollArea.Autosize>
          </Alert>
        )}
        {preview && (preview.hidden > 0 || preview.paid > 0) && (
          <Text size="sm" c="dimmed">
            Hidden: {[
              preview.paid > 0 && `${preview.paid} already paid (tick "Include already paid" to see them)`,
              preview.hidden > 0 && `${preview.hidden} already added and unchanged`,
            ].filter(Boolean).join(', ')}.
          </Text>
        )}

        {preview && (preview.items.length === 0 ? (
          <Text c="dimmed">Nothing new or changed.</Text>
        ) : (
          <ScrollArea.Autosize mah="55vh">
            <Table striped highlightOnHover stickyHeader>
              <Table.Thead>
                <Table.Tr>
                  <Table.Th>
                    <Checkbox
                      checked={allSelected}
                      indeterminate={!allSelected && selected.size > 0}
                      disabled={!selectableKeys.length}
                      onChange={() => setSelected(new Set(allSelected ? [] : selectableKeys))}
                    />
                  </Table.Th>
                  <Table.Th>Type</Table.Th>
                  <Table.Th>Investment</Table.Th>
                  <Table.Th>Market</Table.Th>
                  <Table.Th>Event (ex) date</Table.Th>
                  <Table.Th>Payment date</Table.Th>
                  <Table.Th>Per share / New:Old</Table.Th>
                  <Table.Th>Held by</Table.Th>
                  <Table.Th>Comments</Table.Th>
                  <Table.Th>Status</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {preview.items.map((i) => (
                  <Table.Tr key={i.key}>
                    <Table.Td>
                      <Checkbox checked={selected.has(i.key)} disabled={!selectable(i)} onChange={() => toggle(i.key)} />
                    </Table.Td>
                    <Table.Td><Badge color={i.type === 'Dividend' ? 'teal' : 'grape'} variant="light">{i.type}</Badge></Table.Td>
                    <Table.Td><Text size="sm" fw={500}>{i.investment}</Text><Text size="xs" c="dimmed">{i.description}</Text></Table.Td>
                    <Table.Td>{i.exchange}</Table.Td>
                    <Table.Td>{fmt(i.exDate)}</Table.Td>
                    <Table.Td>{fmt(i.settleDate)}</Table.Td>
                    <Table.Td>{i.type === 'Dividend' ? i.perShareAmount : `${i.newShares}:${i.oldShares}`}</Table.Td>
                    <Table.Td>
                      <Badge color={i.holdingPortfolios ? 'green' : 'gray'} variant="light">
                        {i.holdingPortfolios ? `${i.holdingPortfolios} portfolio${i.holdingPortfolios === 1 ? '' : 's'}` : 'None'}
                      </Badge>
                    </Table.Td>
                    <Table.Td><Text size="xs">{i.comments}</Text></Table.Td>
                    <Table.Td>
                      <Stack gap={4}>
                        {i.missing.length ? <Badge color="red" variant="light">Missing {i.missing.join(', ')}</Badge> :
                          i.existingRequestId ? <Badge color="orange" variant="light">Changed</Badge> :
                            <Badge color="blue" variant="light">New</Badge>}
                        {i.changes?.map((c) => (
                          <Text key={c.field} size="xs">
                            {c.field}: {c.from || '—'} → {c.to || '—'}
                          </Text>
                        ))}
                        {i.existingRequestId && (
                          <Text size="xs" c="dimmed">Updates request {i.existingRequestId} ({i.existingStatus}) and sends it back for approval</Text>
                        )}
                        {i.possibleDuplicates?.length ? (
                          <>
                            <Badge color="yellow" variant="light">Possible duplicate — check the dates</Badge>
                            {i.possibleDuplicates.map((d) => <Text key={d} size="xs" c="dimmed">{d}</Text>)}
                          </>
                        ) : null}
                      </Stack>
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea.Autosize>
        ))}

        {preview && preview.items.length > 0 && (
          <Group justify="flex-end">
            <Button variant="default" onClick={onClose}>Cancel</Button>
            <Button color="green" onClick={add} loading={loading === 'add'} disabled={!selected.size || loading === 'fetch'}>
              Add {selected.size} selected
            </Button>
          </Group>
        )}
      </Stack>
    </Modal>
  );
}
